import React, { useEffect, useState } from 'react';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Icon } from '@iconify/react'
import { useAuth } from '../contexts/AuthContext';
import { usePayment } from '../contexts/PaymentContext';
import { paymentNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import PageHeader from '../components/PageHeader';
import AddPayment from '../components/AddPayment';
import { useMemo } from 'react';

const PaymentList = () => {
  const { getAllPayments, deletePayment } = usePayment();
  const { restaurantId, auth, hasPermission } = useAuth();

  const canCreate = useMemo(() => hasPermission("payment-methods", "create"), [hasPermission]);
  const canUpdate = useMemo(() => hasPermission("payment-methods", "update"), [hasPermission]);
  const canDelete = useMemo(() => hasPermission("payment-methods", "delete"), [hasPermission]);

  const [paymentList, setPaymentList] = useState([])
  const [visible, setVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editPaymentId, setEditPaymentId] = useState(null);
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState(-1);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPayments = async () => {
    try {
      setIsLoading(true);
      const payments = await getAllPayments(currentPage, rowsPerPage, sortField, sortOrder, null);
      if (payments.length !== 0) {
        setPaymentList(payments.data)
        setTotalRecords(payments.totalCount)
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching payments', error);
    }
  };

  useEffect(() => {
    if (restaurantId) {
      fetchPayments();
    }
  }, [currentPage, rowsPerPage, sortField, sortOrder, restaurantId]);

  const handleDelete = async (id) => {
    try {
      const confirmed = await confirmDelete();

      if (confirmed) {
        await deletePayment(id);
        fetchPayments();
        handleClose();
      }
    } catch (error) {
      console.error('Error during delete operation', error);
    }
  }

  const confirmDelete = async () => {
    return new Promise((resolve) => {
      confirmDialog({
        message: 'Are you sure you want to delete this payment?',
        header: 'Delete Confirmation',
        icon: 'pi pi-info-circle',
        position: 'top',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  };

  const handleClose = () => {
    setVisible(false);
    setEditMode(false);
    setEditPaymentId(null);
  };

  const handleSorting = async (e) => {
    const field = e.sortField;
    const order = e.sortOrder;

    setSortField(field);
    setSortOrder(order);
    fetchPayments()
  };

  const onPageChange = (event) => {
    const newCurrentPage = Math.floor(event.first / event.rows) + 1;
    setCurrentPage(newCurrentPage);
    const newRowsPerPage = event.rows;
    setRowsPerPage(newRowsPerPage);
  };

  return (
    <Layout2 items={paymentNav.items} navTitle={paymentNav.header}>
      <div className="d-flex flex-column w-100">
        <PageHeader
          title={'Payment Types'}
          buttonLable={canCreate ? 'Add New Payment' : ''}
          buttonOnClick={canCreate ? () => setVisible(true) : undefined}
        />
        <div className='px-4 py-2'>
          <ConfirmDialog />
          {(visible || (editMode && editPaymentId)) && (
            <AddPayment
              visible={visible}
              editMode={editMode}
              editPaymentId={editPaymentId}
              handleClose={handleClose}
              fetchPayments={fetchPayments}
            />
          )}
          {isLoading ? (
            <Loader />
          ) : (
            <DataTable
              className="customerTable thead-dark"
              totalRecords={totalRecords}
              lazy
              paginator
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={(e) => {
                if (!e.sortField && e.sortOrder === 0) {
                  handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                } else {
                  handleSorting(e);
                }
              }}
              removableSort
              rows={rowsPerPage}
              value={paymentList}
              first={(currentPage - 1) * rowsPerPage}
              onPage={onPageChange}
              dataKey="id"
              emptyMessage="No payment found."
            >
              <Column
                field="name"
                header="Name"
                sortable
                filterField="name"
                body={(rowData) => (
                  <div className='name' title={rowData.name}>
                    <Icon icon="ic:baseline-payment" style={{ color: 'black', marginRight: '4px' }} />
                    {rowData.name}
                  </div>
                )}
              />
              {(canUpdate || canDelete) &&
                <Column
                  header="Action"
                  className='action_td'
                  align="left"
                  body={(rowData) => (
                    !["Add to Tab", "Credit Card", "Cash", "Bank Transfer", "Split Bill", "Deposit"].includes(rowData.name) ? (
                      <>
                        {canUpdate &&
                          <i
                            className="pi pi-pencil pointerCursor"
                            title="Edit"
                            onClick={() => {
                              setEditPaymentId(rowData.id);
                              setEditMode(true);
                            }}
                          />}
                        {
                          canDelete &&
                          <i
                            className="pi pi-trash ms-2 pointerCursor"
                            title="Delete"
                            aria-label="Cancel"
                            onClick={() => handleDelete(rowData.id)}
                          />
                        }
                      </>
                    ) : ('')
                  )}
                />
              }
            </DataTable>
          )}
        </div>
      </div>
    </Layout2>
  );
};

export default PaymentList;
